import React, { useState } from 'react';

const AuditPlanResultsSection = ({ results }) => {
  const [activeTab, setActiveTab] = useState('tables');

  if (!results || !results.llm_response) {
    return null;
  }

  // Parse markdown tables from LLM response
  const parseMarkdownTables = (text) => {
    const tables = [];
    const lines = text.split('\n');
    let currentTable = null;
    let tableTitle = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for table title (lines that contain "Tabel A", "Tabel B", etc.)
      if (line.includes('Tabel A') || line.includes('Tabel B') || 
          line.includes('Table') || line.includes('tabel')) {
        tableTitle = line;
        continue;
      }
      
      // Check if line is a table header (contains |)
      if (line.includes('|') && line.split('|').length > 2) {
        if (currentTable === null) {
          currentTable = {
            title: tableTitle || `Table ${tables.length + 1}`,
            headers: [],
            rows: []
          };
          
          // Parse headers
          const headerCells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
          currentTable.headers = headerCells;
          
          // Skip separator line
          if (i + 1 < lines.length && lines[i + 1].includes('---')) {
            i++; // Skip the separator line
          }
        } else {
          // Parse data row
          const rowCells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
          if (rowCells.length > 0 && rowCells[0] !== '') {
            currentTable.rows.push(rowCells);
          }
        }
      } else if (currentTable !== null && line === '') {
        // End of table
        if (currentTable.rows.length > 0) {
          tables.push(currentTable);
        }
        currentTable = null;
        tableTitle = '';
      }
    }
    
    // Add last table if exists
    if (currentTable !== null && currentTable.rows.length > 0) {
      tables.push(currentTable);
    }
    
    return tables;
  };

  const tables = parseMarkdownTables(results.llm_response);

  const downloadExcel = () => {
    let csvContent = '';
    
    if (results.report_type === 'report1') {
      // For Report 1, create 2 sections: Tabel A and Tabel B
      const tableA = tables.find(t => 
        t.title.toLowerCase().includes('audit') || 
        t.title.toLowerCase().includes('tabel a') ||
        t.title.toLowerCase().includes('wajib')
      );
      const tableB = tables.find(t => 
        t.title.toLowerCase().includes('teknologi') || 
        t.title.toLowerCase().includes('tabel b') ||
        t.title.toLowerCase().includes('keamanan')
      );

      if (tableA) {
        csvContent += '=== TABEL A - AUDIT WAJIB TAHUNAN ===\n\n';
        csvContent += convertTableToCSV(tableA);
        csvContent += '\n\n';
      }

      if (tableB) {
        csvContent += '=== TABEL B - AREA TEKNOLOGI DAN PENGUJIAN KEAMANAN ===\n\n';
        csvContent += convertTableToCSV(tableB);
        csvContent += '\n\n';
      }

      // If tables not found by title, use first two tables
      if (!tableA && !tableB && tables.length >= 2) {
        csvContent += '=== TABEL A - AUDIT WAJIB TAHUNAN ===\n\n';
        csvContent += convertTableToCSV(tables[0]);
        csvContent += '\n\n';
        
        csvContent += '=== TABEL B - AREA TEKNOLOGI DAN PENGUJIAN KEAMANAN ===\n\n';
        csvContent += convertTableToCSV(tables[1]);
        csvContent += '\n\n';
      } else if (tables.length === 1) {
        csvContent += '=== AUDIT ANALYSIS RESULTS ===\n\n';
        csvContent += convertTableToCSV(tables[0]);
        csvContent += '\n\n';
      }
    } else {
      // For Report 2, create single section
      if (tables.length > 0) {
        csvContent += '=== HIGH RISK PROCESSES ANALYSIS ===\n\n';
        csvContent += convertTableToCSV(tables[0]);
        csvContent += '\n\n';
      }
    }

    // If no tables found, use raw text
    if (csvContent === '') {
      csvContent = results.llm_response;
    }

    // Create CSV file (which can be opened in Excel)
    const BOM = '\uFEFF'; // UTF-8 BOM for proper Excel encoding
    const csvData = BOM + csvContent;
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = `audit-analysis-${results.report_type}-${Date.now()}.csv`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const convertTableToCSV = (table) => {
    let csv = '';
    
    // Add headers
    if (table.headers && table.headers.length > 0) {
      csv += table.headers.map(header => `"${header.replace(/"/g, '""')}"`).join(',') + '\n';
    }

    // Add rows
    if (table.rows && table.rows.length > 0) {
      table.rows.forEach(row => {
        const csvRow = row.map(cell => {
          // Clean and escape cell content
          const cleanCell = String(cell || '').replace(/"/g, '""');
          return `"${cleanCell}"`;
        }).join(',');
        csv += csvRow + '\n';
      });
    }

    return csv;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(results.llm_response).then(() => {
      alert('Results copied to clipboard!');
    });
  };

  const getReportTypeIcon = (reportType) => {
    switch (reportType) {
      case 'report1':
        return 'fa-balance-scale text-blue-600';
      case 'report2':
        return 'fa-exclamation-triangle text-red-600';
      default:
        return 'fa-clipboard-check text-purple-600';
    }
  };

  const getReportTypeLabel = (reportType) => {
    switch (reportType) {
      case 'report1':
        return 'Report 1 - Regulatory Analysis';
      case 'report2':
        return 'Report 2 - High Risk Process Analysis';
      default:
        return 'Audit Analysis';
    }
  };

  return (
    <section className="mb-8">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                <i className={`fa-solid ${getReportTypeIcon(results.report_type)} mr-3`}></i>
                {getReportTypeLabel(results.report_type)} Results
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Analysis completed • {results.files_processed} file(s) processed
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={copyToClipboard}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center"
              >
                <i className="fa-solid fa-copy mr-2"></i>
                Copy
              </button>
              <button
                onClick={downloadExcel}
                className="px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center"
                title={results.report_type === 'report1' ? 'Download as CSV with 2 sections: Tabel A & Tabel B' : 'Download as CSV with High Risk Processes table'}
              >
                <i className="fa-solid fa-file-excel mr-2"></i>
                Excel/CSV
              </button>
              <button
                onClick={() => {
                  const element = document.createElement('a');
                  const file = new Blob([results.llm_response], { type: 'text/markdown' });
                  element.href = URL.createObjectURL(file);
                  element.download = `audit-analysis-${results.report_type}-${Date.now()}.md`;
                  document.body.appendChild(element);
                  element.click();
                  document.body.removeChild(element);
                }}
                className="px-3 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center"
              >
                <i className="fa-solid fa-download mr-2"></i>
                Markdown
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('tables')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'tables'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="fa-solid fa-table mr-2"></i>
              Structured Tables ({tables.length})
            </button>
            <button
              onClick={() => setActiveTab('raw')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'raw'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="fa-solid fa-code mr-2"></i>
              Raw Response
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'tables' && (
            <div className="space-y-8">
              {tables.length > 0 ? (
                tables.map((table, tableIndex) => (
                  <div key={tableIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h4 className="font-medium text-gray-800">{table.title}</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {table.headers.map((header, headerIndex) => (
                              <th
                                key={headerIndex}
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              {row.map((cell, cellIndex) => (
                                <td
                                  key={cellIndex}
                                  className="px-4 py-3 text-sm text-gray-900 whitespace-pre-wrap"
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <i className="fa-solid fa-table text-4xl text-gray-300 mb-4"></i>
                  <h4 className="text-lg font-medium text-gray-500 mb-2">No Tables Found</h4>
                  <p className="text-gray-400">
                    The analysis response doesn't contain structured tables. 
                    Check the raw response tab for the complete output.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'raw' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                  <i className="fa-solid fa-info-circle mr-2 text-blue-500"></i>
                  Analysis Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Report Type:</span>
                    <div className="text-gray-800">{getReportTypeLabel(results.report_type)}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Files Processed:</span>
                    <div className="text-gray-800">{results.files_processed}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Processing Method:</span>
                    <div className="text-gray-800">{results.processing_method}</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-white flex items-center">
                    <i className="fa-solid fa-terminal mr-2"></i>
                    Complete Analysis Response
                  </h4>
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
                  >
                    Copy All
                  </button>
                </div>
                <pre className="text-sm text-gray-300 whitespace-pre-wrap overflow-x-auto">
                  {results.llm_response}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AuditPlanResultsSection;
